import * as React from 'react';
import { useCallback, useContext, useRef } from 'react';
import { get } from 'lodash';
import { windowWrapper } from '@clubmed/utils';
import { ID_MODAL_HEADER__BE__SHARE_CONTEXT } from 'Components/utils/identifiers/constants';
import { ICONS } from 'Components/utils/icon/icons';
import responsiveness from 'Components/utils/responsive/Responsive';
import { B2CAppContext } from 'Context/ReactAppContext';
import config from 'Config/index';
import {
  trackShareContextDesktopPopin,
  trackShareContextMobilePopin,
} from 'Views/booking/BookingEngine.tracking';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Button } from 'Components/frameworks/button/Button';
import { Modal } from 'Components/frameworks/modal/Modal';
type Props = {
  i18n: CMSContent;
  responsive: ResponsiveProps;
  onToggleModal: () => any;
  displayToastOnContextShare: (toastLabel: string) => any;
  actionGA: string;
  currentPage: string;
};
export function ShareContext({
  i18n,
  responsive,
  onToggleModal,
  displayToastOnContextShare,
  actionGA,
  currentPage,
}: Props) {
  const context = useContext(B2CAppContext);
  const textInput = useRef<HTMLInputElement | null>(null);

  const onCopyLinkClick = useCallback(() => {
    textInput.current.select();
    document.execCommand('copy');
    trackShareContextDesktopPopin(actionGA, context);
    displayToastOnContextShare(get(i18n, 'booking.common.share.linkCopied'));
  }, [actionGA, context, displayToastOnContextShare, i18n]);

  const handleOnNavigatorShareClick = useCallback(() => {
    if (navigator.share) {
      trackShareContextDesktopPopin(actionGA, context);
      navigator
        .share({
          url: windowWrapper.getLocation(),
        })
        .then(() => {
          onToggleModal();
        });
    }
  }, [actionGA, context, onToggleModal]);

  const handleMediaLinkClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      const msg = windowWrapper.getLocation();
      const { media } = event.currentTarget.dataset;
      trackShareContextMobilePopin(actionGA, media, context);

      if (media === 'messenger') {
        return window.open(
          `fb-messenger://share/?link=${encodeURIComponent(msg)}&app_id=${config.get('facebookMessenger.appId')}`,
          '_self',
        );
      }

      if (media === 'whatsapp') {
        return window.open(`whatsapp://send?text=${encodeURIComponent(msg)}`, '_self');
      }

      if (media === 'sms') {
        return window.open(`sms:?&body=${encodeURIComponent(msg)}`, '_self');
      }
    },
    [actionGA, context],
  );

  return (
    <Modal
      ariaLabel={get(i18n, 'booking.common.share.title')}
      ariaLabelledby={ID_MODAL_HEADER__BE__SHARE_CONTEXT}
      closeLabel={get(i18n, 'common.close')}
      onClose={onToggleModal}
      size="small"
    >
      <h1
        className="flex items-center gap-10 border-b-1 border-lightGrey p-20 text-20"
        id={ID_MODAL_HEADER__BE__SHARE_CONTEXT}
      >
        <Icon className="text-sienna" name={ICONS.SHARE} width="2rem" />
        {get(i18n, 'booking.common.share.title')}
      </h1>
      <div className="max-w-full overflow-hidden bg-white p-20">
        <input // this input should not be hidden otherwise the copy func will not work
          className="absolute -z-1 h-0"
          ref={textInput}
          value={windowWrapper.getLocation()}
          readOnly
          aria-hidden="true"
        />
        {responsive.isTablet ? (
          <React.Fragment>
            <p className="text-16">{get(i18n, 'booking.common.share.info')}</p>
            <p className="mt-5 text-12">{get(i18n, 'booking.common.share.copyPaste')}</p>
            <div className="mt-10 flex items-center" data-testid="ShareContext-shareLink">
              <button
                className="w-full max-w-full truncate text-14 underline"
                data-testid="ShareContext-desktopUrl"
                onClick={onCopyLinkClick}
                type="button"
              >
                {windowWrapper.getLocation()}
              </button>
              <button onClick={onCopyLinkClick} type="button">
                <Icon
                  className="rounded-full bg-darkGrey p-10 text-white"
                  name={ICONS.LINK}
                  width="3rem"
                />
              </button>
            </div>
          </React.Fragment>
        ) : (
          <div className="flex flex-col items-center" data-testid="ShareContext-mobileContent">
            <p className="my-20 px-20 text-center text-14">
              {get(i18n, 'booking.common.share.info')}
            </p>
            {navigator.share ? (
              <Button
                className="mb-10"
                data-testid="ShareContext-shareButton"
                onClick={handleOnNavigatorShareClick}
              >
                {get(i18n, 'booking.common.share.label')}
              </Button>
            ) : (
              <div className="flex w-full justify-center gap-10 border-t-1 border-lightGrey py-25">
                <button
                  type="button"
                  data-media="messenger"
                  onClick={handleMediaLinkClick}
                  data-testid="ShareContext-messenger"
                >
                  <Icon
                    className="rounded-full bg-[#1984ff] p-10 text-white"
                    name={ICONS.MESSENGER}
                    width="3rem"
                  />
                </button>
                <button
                  type="button"
                  data-media="whatsapp"
                  onClick={handleMediaLinkClick}
                  data-testid="ShareContext-whatsapp"
                >
                  <Icon
                    className="rounded-full bg-[#52cc5d] p-10 text-white"
                    name={ICONS.WHATSAPP}
                    width="3rem"
                  />
                </button>
                <button
                  type="button"
                  data-media="sms"
                  onClick={handleMediaLinkClick}
                  data-testid="ShareContext-12s"
                >
                  <Icon
                    className="rounded-full bg-[#f69a3f] p-10 text-white"
                    name={ICONS.SMS}
                    width="3rem"
                  />
                </button>
                <button type="button" onClick={onCopyLinkClick}>
                  <Icon
                    className="rounded-full bg-darkGrey p-10 text-white"
                    name={ICONS.LINK}
                    width="3rem"
                  />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
export default responsiveness(ShareContext);
